<main>
  <loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>

  <header>
    <div class="headline">
      <h1>
        {{ isEditMode() ? ('ProductRecipe_Edit' | translateWF: 'Edit Product Recipe') : ('ProductRecipe_Create' | translateWF: 'Create Product Recipe') }}
      </h1>
      @if (isEditMode()) {
        <csl-dropdown [enumObject]="statusesEnum()!" [enumValueOptions]="productRecipeStatusOptions()" [formControl]="status" />
      }
    </div>
    <div class="buttons">
      @if (canEdit()) {
        <p-splitbutton [label]="'Common_Save' | translateWF: 'Save'" [disabled]="formGroup.invalid && ingredients.invalid" (onClick)="onSave(false)" [model]="saveMenuItems" />
      }
      <p-button [label]="'Common_Cancel' | translateWF: 'Cancel'" variant="outlined" (click)="onCancel()" />
    </div>
  </header>

  <div class="content">
    <div class="col">
      @if (userLite()) {
        <p-panel header="Notes">
          <csl-standard-notes [notes]="notes()" [userLite]="userLite()!" [viewOnly]="!canEdit()" (viewMore)="onViewMore()" />
        </p-panel>
      }

      <form [formGroup]="formGroup">
        <p-panel [header]="'ProductRecipe_General_Information' | translateWF: 'General Information'">
          <p-card>
            <div class="grid-12">
              <div class="span-3">
                <div class="form-box">
                  <label for="recipeNumber">
                    {{ 'ProductRecipe_Number' | translateWF: 'Recipe Number' }}
                  </label>
                  <input id="recipeNumber" type="text" pInputText formControlName="recipeNumber" [readonly]="true" class="w-full p-disabled" />
                </div>
              </div>

              <div class="span-3">
                <div class="form-box">
                  <label for="name" class="required">
                    {{ 'ProductRecipe_Name' | translateWF: 'Name' }}
                  </label>
                  <input id="name" type="text" pInputText formControlName="name" [placeholder]="'Enter_recipe_name' | translateWF: 'Enter recipe name'" class="w-full" />
                </div>
              </div>

              <div class="span-3">
                <div class="form-box">
                  <label for="outputItemCode">
                    {{ 'ProductRecipe_OutputItemCode' | translateWF: 'Item Master Code' }}
                  </label>
                  <input
                    id="outputItemCode"
                    type="text"
                    pInputText
                    [value]="outputItemCode.value || ''"
                    [placeholder]="'ProductRecipe_OutputItemCode_NotLinked' | translateWF: 'Linked when an Item Master is created'"
                    [readonly]="true"
                    class="w-full p-disabled"
                  />
                </div>
              </div>

              <!-- Output UOM (hardcoded to kg) -->
              <div class="span-3">
                <div class="form-box">
                  <label for="outputUOM">
                    {{ 'ProductRecipe_OutputUOM' | translateWF: 'UOM' }}
                  </label>
                  <input id="outputUOM" type="text" pInputText [value]="'kg'" [readonly]="true" class="w-full p-disabled" />
                </div>
              </div>

              <!-- Chemical Role: settable on creation, locked once persisted -->
              <div class="span-3">
                <div class="form-box">
                  <label for="chemicalRole" class="required">
                    {{ 'ProductRecipe_ChemicalRole' | translateWF: 'Chemical Role' }}
                  </label>
                  <p-select
                    id="chemicalRole"
                    formControlName="chemicalRole"
                    [options]="chemicalRoleOptions()"
                    [placeholder]="'ProductRecipe_ChemicalRole_Placeholder' | translateWF: 'Select role'"
                    appendTo="body"
                    styleClass="w-full"
                    class="w-full"
                  />
                  @if (isEditMode()) {
                    <small class="text-gray-500">
                      {{
                        'ProductRecipe_ChemicalRole_Locked'
                          | translateWF
                            : 'Locked after creation. Create a new recipe to use
                    a different role.'
                      }}
                    </small>
                  }
                </div>
              </div>
            </div>

            <div class="grid-12 mt-4">
              <!-- Base Quantity (auto-calculated from ingredients total) -->
              <div class="span-3">
                <div class="form-box">
                  <label for="baseQuantity">
                    {{ 'ProductRecipe_BaseQuantity' | translateWF: 'Base Quantity' }}
                  </label>
                  <input id="baseQuantity" type="text" pInputText [value]="(getTotalQuantity() | number: '1.1-4') + ' kg'" [readonly]="true" class="w-full p-disabled" />
                </div>
              </div>

              <div class="span-3">
                <div class="form-box">
                  <label for="waterConsumptionInLiters">
                    {{ 'ProductRecipe_WaterConsumption' | translateWF: 'Water Consumption (L)' }}
                  </label>
                  <p-inputNumber
                    id="waterConsumptionInLiters"
                    formControlName="waterConsumptionInLiters"
                    [minFractionDigits]="0"
                    [maxFractionDigits]="2"
                    [min]="0"
                    styleClass="w-full"
                    class="w-full"
                    [placeholder]="'Enter_water_consumption' | translateWF: 'Enter water consumption'"
                  />
                </div>
              </div>
            </div>
          </p-card>
        </p-panel>
      </form>

      <p-panel [header]="'ProductRecipe_Ingredients' | translateWF: 'Ingredients'">
        <ng-template pTemplate="icons">
          <div class="row-start-center">
            @if (canEdit() && !isLockedStatus()) {
              <div class="row-sm-start-center">
                <span class="text-sm">{{ 'ProductRecipe_ItemMaster' | translateWF: 'Item Master' }}</span>
                <p-toggleSwitch [ngModel]="searchByRecipe()" (ngModelChange)="searchByRecipe.set($event)" />
                <span class="text-sm">{{ 'ProductRecipe_Recipe' | translateWF: 'Recipe' }}</span>

                @if (!searchByRecipe()) {
                  <p-autoComplete
                    #itemSearch1="cslServerSearch"
                    [cslServerSearch]="fetchItems"
                    [suggestions]="itemSearch1.results()"
                    optionLabel="itemCode"
                    [dropdown]="true"
                    dropdownIcon="pi pi-search"
                    [forceSelection]="true"
                    appendTo="body"
                    (onSelect)="addIngredientByItem($event.value)"
                  >
                    <ng-template let-item pTemplate="item">
                      <span
                        ><strong>({{ item.itemCode }})</strong> {{ item.itemLongDisplayName | localized }}</span
                      >
                    </ng-template>
                  </p-autoComplete>
                } @else {
                  <p-autoComplete
                    #recipeSearch1="cslServerSearch"
                    [cslServerSearch]="fetchRecipes"
                    [suggestions]="recipeSearch1.results()"
                    optionLabel="name"
                    [dropdown]="true"
                    dropdownIcon="pi pi-search"
                    [forceSelection]="true"
                    appendTo="body"
                    (onSelect)="addIngredientByRecipe($event.value)"
                  >
                    <ng-template let-recipe pTemplate="item">
                      <div class="col">
                        <span class="font-semibold">{{ recipe.number }}</span>
                        <span class="text-sm text-gray-600">{{ recipe.name }}</span>
                      </div>
                    </ng-template>
                  </p-autoComplete>
                }
              </div>
            }
            <div class="row">
              <span class="row-sm-start-center text-sm"> <i class="pi pi-box text-blue-500"></i> {{ 'ProductRecipe_ItemMaster' | translateWF: 'Item Master' }} </span>
              <span class="row-sm-start-center text-sm"> <i class="pi pi-book text-green-500"></i> {{ 'ProductRecipe_Recipe' | translateWF: 'Recipe' }} </span>
            </div>
          </div>
        </ng-template>
        <p-card data-variant="compact">
          <p-table [value]="ingredients.controls" [tableStyle]="{ 'min-width': '50rem' }">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 16rem">{{ 'ProductRecipe_Code' | translateWF: 'Code' }}</th>
                <th>{{ 'ProductRecipe_Name' | translateWF: 'Name' }}</th>
                <th>{{ 'ProductRecipe_Quantity' | translateWF: 'Quantity (kg)' }}</th>
                <th>{{ 'ProductRecipe_Substitutes' | translateWF: 'Substitutes' }}</th>
                @if (canEdit() && !isLockedStatus()) {
                  <th style="width: 5rem">{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
                }
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-row let-i="rowIndex">
              <tr [formGroup]="row">
                <td>
                  <div class="row-sm-start-center whitespace-nowrap">
                    @if (row.controls.itemCode.value) {
                      <i class="pi pi-box text-blue-500 w-5 h-5 row-center-center"></i>
                    } @else {
                      <i class="pi pi-book text-green-500 w-5 h-5 row-center-center"></i>
                    }

                    @if (row.controls.productRecipeNumber.value) {
                      <button
                        type="button"
                        (click)="toggleIngredientExpansion(row)"
                        class="row-center-center p-0 h-6 w-6 border-none bg-transparent shadow-none outline-none cursor-pointer text-gray-500 hover:text-gray-700"
                        [pTooltip]="'ProductRecipe_ToggleSubRows' | translateWF: 'Toggle Sub-rows'"
                      >
                        <i [class]="isIngredientExpanded(row) ? 'pi pi-chevron-down text-xs' : 'pi pi-chevron-right text-xs'"></i>
                      </button>
                    } @else {
                      <div class="w-6 h-6 row-center-center"></div>
                      <!-- Spacer to align codes perfectly -->
                    }

                    <span class="font-bold">{{ row.controls.itemCode.value || row.controls.productRecipeNumber.value || '-' }}</span>
                  </div>
                </td>
                <td>
                  <span>{{ row.controls.displayName.value || '-' }}</span>
                </td>
                <td>
                  <p-inputNumber formControlName="quantity" [minFractionDigits]="1" [maxFractionDigits]="4" [min]="0.01" styleClass="w-full" class="w-full" />
                </td>
                <td>
                  <span class="text-sm">{{ getSubstitutes(i).length }} {{ 'ProductRecipe_substitute_count' | translateWF: 'substitute(s)' }}</span>
                  @if (canEdit() && !isLockedStatus()) {
                    <p-button
                      icon="pi pi-plus"
                      severity="secondary"
                      [text]="true"
                      size="small"
                      [pTooltip]="'ProductRecipe_AddSubstitute' | translateWF: 'Add Substitute'"
                      (onClick)="addSubstitute(i)"
                    />
                  }
                </td>
                @if (canEdit() && !isLockedStatus()) {
                  <td>
                    <p-button icon="pi pi-trash" severity="danger" [text]="true" (onClick)="removeIngredient(i)" />
                  </td>
                }
              </tr>

              <!-- Nested sub-recipes & item masters sub-rows -->
              @if (row.controls.productRecipeNumber.value && isIngredientExpanded(row)) {
                @for (nestedIng of getSubIngredients(row.controls.productRecipeNumber.value); track nestedIng) {
                  <ng-container *ngTemplateOutlet="subRowTemplate; context: { ing: nestedIng, level: 1 }"></ng-container>
                }
              }

              <!-- Substitutes sub-rows -->
              @for (subControl of getSubstitutes(i).controls; track subControl; let j = $index) {
                <tr [formGroup]="subControl" class="bg-gray-50">
                  <td class="pl-8" colspan="2">
                    <div class="row-start-center">
                      <i class="pi pi-arrow-right text-gray-400 text-xs"></i>
                      @if (subControl.controls.itemCode.value || subControl.controls.productRecipeNumber.value) {
                        <span class="row-start-center">
                          @if (subControl.controls.itemCode.value) {
                            <i class="pi pi-box text-blue-500"></i>
                          } @else {
                            <i class="pi pi-book text-green-500"></i>
                          }
                          <span class="font-bold">{{ subControl.controls.itemCode.value || subControl.controls.productRecipeNumber.value }}</span>
                          {{ subControl.controls.displayName.value }}
                        </span>
                      } @else if (!isLockedStatus()) {
                        @if (isRecipeIngredient(i)) {
                          <p-autoComplete
                            #recipeSearch2="cslServerSearch"
                            [cslServerSearch]="fetchRecipes"
                            [suggestions]="recipeSearch2.results()"
                            optionLabel="name"
                            [placeholder]="'Sub_Recipe_Ref' | translateWF: 'Search Substitute Recipe'"
                            [dropdown]="true"
                            dropdownIcon="pi pi-search"
                            [forceSelection]="true"
                            appendTo="body"
                            (onSelect)="onSubstituteRecipeSelected($event, i, j)"
                            styleClass="w-full"
                            class="w-full"
                          >
                            <ng-template let-recipe pTemplate="item">
                              <div class="col">
                                <span class="font-semibold">{{ recipe.number }}</span>
                                <span class="text-sm text-gray-600">{{ recipe.name }}</span>
                              </div>
                            </ng-template>
                          </p-autoComplete>
                        } @else {
                          <p-autoComplete
                            #itemSearch2="cslServerSearch"
                            [cslServerSearch]="fetchItems"
                            [suggestions]="itemSearch2.results()"
                            optionLabel="itemCode"
                            [placeholder]="'Sub_Item_Code' | translateWF: 'Search Substitute Item'"
                            [dropdown]="true"
                            dropdownIcon="pi pi-search"
                            [forceSelection]="true"
                            appendTo="body"
                            (onSelect)="onSubstituteItemSelected($event, i, j)"
                            styleClass="w-full"
                            class="w-full"
                          >
                            <ng-template let-item pTemplate="item">
                              <span
                                ><strong>({{ item.itemCode }})</strong> {{ item.itemLongDisplayName | localized }}</span
                              >
                            </ng-template>
                          </p-autoComplete>
                        }
                      }
                    </div>
                  </td>
                  <td>
                    <p-inputNumber formControlName="priority" [min]="0" styleClass="w-full" class="w-full" [placeholder]="'Priority' | translateWF: 'Priority'" />
                  </td>
                  <td></td>
                  @if (canEdit() && !isLockedStatus()) {
                    <td>
                      <p-button icon="pi pi-times" severity="secondary" [text]="true" size="small" (onClick)="removeSubstitute(i, j)" />
                    </td>
                  }
                </tr>
              }
            </ng-template>

            <!-- Recursive template for rendering nested sub-recipes and item masters -->
            <ng-template #subRowTemplate let-ing="ing" let-level="level">
              <tr class="bg-slate-50/50 hover:bg-slate-100/50 transition-colors">
                <td [style.padding-left.px]="level * 24 + 16">
                  <div class="row-sm-start-center whitespace-nowrap">
                    @if (ing.itemCode) {
                      <i class="pi pi-box text-blue-400 text-sm w-4 h-4 row-center-center"></i>
                    } @else {
                      <i class="pi pi-book text-green-400 text-sm w-4 h-4 row-center-center"></i>
                    }

                    @if (ing.itemCode) {
                      <div class="w-5 h-5 row-center-center"></div>
                      <!-- Spacer for items (no chevrons) -->
                    } @else {
                      @if (hasSubIngredients(ing.productRecipeNumber)) {
                        <button
                          type="button"
                          (click)="toggleSubRecipeExpansion(ing.productRecipeNumber)"
                          class="row-center-center p-0 h-5 w-5 border-none bg-transparent shadow-none outline-none cursor-pointer text-gray-500 hover:text-gray-700"
                        >
                          <i [class]="isSubRecipeExpanded(ing.productRecipeNumber) ? 'pi pi-chevron-down text-[10px]' : 'pi pi-chevron-right text-[10px]'"></i>
                        </button>
                      } @else {
                        <div class="w-5 h-5 row-center-center"></div>
                        <!-- Spacer for recipes without sub-ingredients -->
                      }
                    }

                    <span class="font-semibold text-gray-700 text-sm">{{ ing.itemCode || ing.productRecipeNumber }}</span>
                  </div>
                </td>
                <td>
                  @if (ing.itemCode) {
                    <span class="text-sm text-gray-600 italic">{{ itemNamesMap().get(ing.itemCode) || ('Common_Loading' | translateWF: 'Loading...') }}</span>
                  } @else {
                    <span class="text-sm text-gray-600 italic">{{ subRecipesMap().get(ing.productRecipeNumber)?.name || ('Common_Loading' | translateWF: 'Loading...') }}</span>
                  }
                </td>
                <td>
                  <span class="text-sm text-gray-600 font-medium">{{ ing.quantity | number: '1.1-4' }} kg</span>
                </td>
                <td>
                  <!-- Nested sub-recipes do not have root-level substitute management here -->
                </td>
                <td></td>
              </tr>

              <!-- If it's a recipe and expanded, recursively render its ingredients -->
              @if (ing.productRecipeNumber && isSubRecipeExpanded(ing.productRecipeNumber)) {
                @for (nestedIng of getSubIngredients(ing.productRecipeNumber); track nestedIng) {
                  <ng-container *ngTemplateOutlet="subRowTemplate; context: { ing: nestedIng, level: level + 1 }"></ng-container>
                }
              }
            </ng-template>

            <ng-template pTemplate="footer">
              <tr>
                <td colspan="2" class="text-right font-bold">
                  {{ 'ProductRecipe_Total' | translateWF: 'Total' }}
                </td>
                <td class="font-bold">{{ getTotalQuantity() | number: '1.1-4' }} kg</td>
                <td colspan="2"></td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="5" class="text-center">
                  {{
                    'ProductRecipe_NoIngredients'
                      | translateWF
                        : 'No ingredients added. Use the search fields above to add
                  an ingredient by item code or recipe.'
                  }}
                </td>
              </tr>
            </ng-template>
          </p-table>
        </p-card>
      </p-panel>
    </div>
  </div>
</main>
